import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  Trophy, Sparkles, Loader2, ArrowLeft, Plane, Wallet, CalendarClock,
  Check, AlertTriangle, Target, MapPin, Gauge, ShieldCheck,
} from "lucide-react";
import { Shell } from "../components/Shell";
import { api, eur } from "../lib/api";
import { useI18n } from "../i18n";
import { toast } from "sonner";
import { saveSelectedXpPlan } from "../lib/xpPlan";

const isoPlusDays = (days) => {
  const d = new Date();
  d.setHours(12, 0, 0, 0);
  d.setDate(d.getDate() + days);
  return d.toISOString().slice(0, 10);
};

const fmtDate = (value, lang) => {
  if (!value) return "—";
  try {
    return new Date(`${value}T12:00:00`).toLocaleDateString(lang === "fr" ? "fr-FR" : "en-GB", {
      day: "numeric", month: "short",
    });
  } catch { return value; }
};

export default function Loyalty() {
  const navigate = useNavigate();
  const { t, lang } = useI18n();
  const [xp, setXp] = useState(82);
  const [budget, setBudget] = useState(900);
  const [origin, setOrigin] = useState("CDG");
  const [deadline, setDeadline] = useState(isoPlusDays(35));
  const [windowStart, setWindowStart] = useState(isoPlusDays(7));
  const [windowEnd, setWindowEnd] = useState(isoPlusDays(28));
  const [tripDays, setTripDays] = useState(1);
  const [cabins, setCabins] = useState(["economy", "business"]);
  const [evaluating, setEvaluating] = useState(false);
  const [result, setResult] = useState(null);

  const toggleCabin = (cabin) => {
    setCabins((prev) => prev.includes(cabin)
      ? (prev.length > 1 ? prev.filter((x) => x !== cabin) : prev)
      : [...prev, cabin]);
  };

  const validDates = windowStart && windowEnd && deadline && windowStart <= windowEnd && windowEnd <= deadline;

  const run = async () => {
    if (!validDates) {
      toast.error(t("loyalty.invalidDates"));
      return;
    }
    setEvaluating(true);
    setResult(null);
    try {
      const r = await api.xpSearch({
        target_xp: Number(xp),
        budget: Number(budget),
        deadline,
        windows: [{ start: windowStart, end: windowEnd }],
        origin: String(origin || "CDG").toUpperCase(),
        cabins,
        trip_days: Number(tripDays),
        max_dates: 4,
        max_destinations: 6,
        max_provider_calls: 12,
        max_offers_per_call: 12,
        max_trips: 3,
      });
      setResult(r);
      setTimeout(() => {
        const node = document.getElementById("loyalty-result");
        if (node && typeof node.scrollIntoView === "function") node.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 100);
    } catch {
      toast.error(t("loyalty.errEval"));
    } finally {
      setEvaluating(false);
    }
  };

  const plans = result?.plans?.plans || [];
  const best = plans[0] || null;
  const pct = best ? Math.min(100, Math.round((best.xp_total / Number(xp || 1)) * 100)) : 0;
  const budgetLeft = best ? Number(budget) - Number(best.total_cost || 0) : null;

  const openPlan = (plan) => {
    saveSelectedXpPlan(plan, {
      target_xp: Number(xp),
      budget: Number(budget),
      deadline,
      origin: String(origin || "CDG").toUpperCase(),
      windows: [{ start: windowStart, end: windowEnd }],
      cabins,
      trip_days: Number(tripDays),
      provider: result?.provider || "Duffel",
    });
    navigate("/loyalty/decision");
  };

  const strategyByPlan = useMemo(() => {
    const out = {};
    const strategies = result?.plans?.strategies || {};
    Object.entries(strategies).forEach(([key, plan]) => {
      if (!plan?.id) return;
      out[plan.id] = [...(out[plan.id] || []), key];
    });
    return out;
  }, [result]);

  return (
    <Shell active="plan">
      <button onClick={() => navigate("/")} className="inline-flex items-center gap-1.5 text-sm text-slate-500 mb-4 hover:text-slate-900 transition-colors"><ArrowLeft size={16} /> Decidova</button>
      <div className="mb-5">
        <div className="text-[11px] font-semibold tracking-[0.24em] font-display" style={{ color: "var(--brand)" }}>{t("loyalty.kicker")}</div>
        <h1 className="mt-2 font-display font-extrabold text-3xl sm:text-4xl tracking-tight">{t("loyalty.title")}</h1>
        <p className="mt-2 text-slate-500 text-sm max-w-2xl">{t("loyalty.subReal")}</p>
      </div>

      <div className="dv-card p-4 sm:p-5 grid sm:grid-cols-2 gap-4">
        <Field label={t("loyalty.xpNeeded")} icon={Target}>
          <input type="number" min="1" value={xp} onChange={(e) => setXp(e.target.value)} data-testid="loyalty-xp" className="w-full font-mono-num text-2xl font-bold text-slate-900 bg-transparent outline-none" />
        </Field>

        <Field label={t("loyalty.budget")} icon={Wallet}>
          <div className="flex items-center gap-1">
            <span className="text-slate-400 text-lg">€</span>
            <input type="number" min="1" value={budget} onChange={(e) => setBudget(e.target.value)} data-testid="loyalty-budget" className="w-full font-mono-num text-2xl font-bold text-slate-900 bg-transparent outline-none" />
          </div>
        </Field>

        <Field label={t("loyalty.origin")} icon={MapPin}>
          <input value={origin} maxLength={3} onChange={(e) => setOrigin(e.target.value.toUpperCase())} data-testid="loyalty-origin"
            className="w-full bg-transparent outline-none font-display font-bold text-xl uppercase" />
        </Field>

        <Field label={t("loyalty.deadline")} icon={CalendarClock}>
          <input type="date" value={deadline} onChange={(e) => setDeadline(e.target.value)} data-testid="loyalty-deadline"
            className="w-full bg-transparent outline-none font-semibold text-slate-800" />
        </Field>

        <Field label={t("loyalty.availability")} icon={CalendarClock} full>
          <div className="grid sm:grid-cols-[1fr_auto_1fr] gap-3 items-end">
            <div>
              <div className="text-[11px] text-slate-400 mb-1">{t("loyalty.availableFrom")}</div>
              <input type="date" value={windowStart} onChange={(e) => setWindowStart(e.target.value)} data-testid="loyalty-window-start"
                className="w-full rounded-xl border border-[var(--line)] px-3 py-2.5 text-sm outline-none" />
            </div>
            <span className="text-slate-300 pb-3">→</span>
            <div>
              <div className="text-[11px] text-slate-400 mb-1">{t("loyalty.availableUntil")}</div>
              <input type="date" min={windowStart || undefined} max={deadline || undefined} value={windowEnd} onChange={(e) => setWindowEnd(e.target.value)} data-testid="loyalty-window-end"
                className="w-full rounded-xl border border-[var(--line)] px-3 py-2.5 text-sm outline-none" />
            </div>
          </div>
          {!validDates && <div className="mt-2 text-xs" style={{ color: "var(--alert)" }}>{t("loyalty.invalidDates")}</div>}
        </Field>

        <Field label={t("loyalty.cabins")} icon={Plane}>
          <div className="flex flex-wrap gap-2">
            {[
              ["economy", t("loyalty.economy")],
              ["premium_economy", t("loyalty.premium")],
              ["business", t("loyalty.business")],
            ].map(([key, label]) => {
              const on = cabins.includes(key);
              return <button key={key} type="button" onClick={() => toggleCabin(key)}
                className="dv-pill px-3 py-2 text-xs"
                style={{ background: on ? "var(--ink)" : "#fff", color: on ? "#fff" : "var(--ink-soft)", border: "1px solid var(--line)" }}>
                {label}{on && <Check size={12} />}
              </button>;
            })}
          </div>
        </Field>

        <Field label={t("loyalty.runDuration")} icon={Gauge}>
          <div className="flex items-center gap-3">
            <input type="range" min={1} max={7} step={1} value={tripDays} onChange={(e) => setTripDays(Number(e.target.value))}
              data-testid="loyalty-trip-days" className="flex-1 accent-[var(--brand)]" />
            <span className="dv-total text-xl w-20 text-right">{tripDays} {t("loyalty.days")}</span>
          </div>
        </Field>
      </div>

      <button onClick={run} disabled={evaluating || !validDates} data-testid="loyalty-evaluate-button"
        className="dv-btn dv-btn-primary w-full text-base py-4 mt-5" style={{ opacity: evaluating || !validDates ? 0.6 : 1 }}>
        {evaluating ? <Loader2 size={17} className="animate-spin" /> : <Sparkles size={17} />}
        {evaluating ? t("loyalty.evaluatingReal") : t("loyalty.evaluateBtn")}
      </button>

      <AnimatePresence>
        {result && (
          <motion.div id="loyalty-result" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} className="mt-6">
            <div className="dv-card p-5 sm:p-6 mb-4">
              <div className="flex flex-wrap items-start justify-between gap-4">
                <div>
                  <div className="text-[11px] text-slate-400">{t("loyalty.searchProof")}</div>
                  <div className="font-display font-extrabold text-3xl mt-1">
                    {plans.length ? t("loyalty.plansFound", { n: plans.length }) : t("loyalty.noPlanTitle")}
                  </div>
                </div>
                <div className="text-right text-xs text-slate-500">
                  <div>{t("loyalty.provider")}: <span className="font-semibold text-slate-800">{result.provider || "Duffel"}</span></div>
                  <div>{t("loyalty.searchesRun", { n: result.search_job_count || 0 })}</div>
                  <div>{t("loyalty.candidates", { n: result.candidate_offer_count || 0 })}</div>
                </div>
              </div>

              {best && (
                <>
                  <div className="mt-5 rounded-2xl p-4" style={{ background: "var(--paper)" }}>
                    <div className="flex items-center justify-between text-sm mb-2">
                      <span className="font-semibold text-slate-600">{t("loyalty.projectedXp")}</span>
                      <span className="font-mono-num font-bold" style={{ color: "var(--book-ink)" }}>{best.xp_total} / {xp} XP</span>
                    </div>
                    <div className="h-2.5 rounded-full overflow-hidden" style={{ background: "var(--line)" }}>
                      <div className="h-full rounded-full transition-all" style={{ width: `${pct}%`, background: "var(--book)" }} />
                    </div>
                    <div className="mt-3 flex flex-wrap gap-x-5 gap-y-1 text-sm">
                      <span>{t("loyalty.totalCost")}: <strong>{eur(best.total_cost)}</strong></span>
                      <span>{t("loyalty.eurPerXp")}: <strong>{best.eur_per_xp != null ? `${best.eur_per_xp} €/XP` : "—"}</strong></span>
                      {budgetLeft != null && <span style={{ color: budgetLeft >= 0 ? "var(--book-ink)" : "var(--alert)" }}>
                        {budgetLeft >= 0 ? t("common.belowBudget", { x: eur(budgetLeft) }) : t("common.overBudget", { x: eur(-budgetLeft) })}
                      </span>}
                    </div>
                  </div>

                  <div className="mt-5">
                    <div className="text-[11px] font-semibold tracking-[0.2em] text-slate-400 font-display mb-2.5">{t("loyalty.realPlans").toUpperCase()}</div>
                    <div className="space-y-3">
                      {plans.slice(0, 3).map((plan, index) => (
                        <PlanCard key={plan.id || index} plan={plan} rank={index} tags={strategyByPlan[plan.id] || []} lang={lang} t={t} onSelect={() => openPlan(plan)} />
                      ))}
                    </div>
                  </div>
                </>
              )}

              {!best && (
                <div className="mt-5 rounded-2xl p-5 flex gap-3" style={{ background: "var(--wait-soft)" }}>
                  <AlertTriangle size={18} color="var(--wait-ink)" className="shrink-0 mt-0.5" />
                  <div>
                    <div className="font-semibold" style={{ color: "var(--wait-ink)" }}>{t("loyalty.noPlanBody")}</div>
                    <div className="mt-1 text-sm text-slate-600">{result.reason ? t("loyalty.reason", { r: result.reason }) : t("loyalty.adjustSearch")}</div>
                  </div>
                </div>
              )}
            </div>

            <div className="dv-card p-4 flex items-start gap-3 text-sm text-slate-600">
              <ShieldCheck size={17} color="var(--brand)" className="shrink-0 mt-0.5" />
              <div>{t("loyalty.projectionNote")}</div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <div className="mt-4 flex items-center justify-center gap-2 text-xs text-slate-400">
        <Trophy size={13} /> {t("loyalty.providerTestNote")}
      </div>
    </Shell>
  );
}

const Field = ({ label, icon: Icon, children, full }) => (
  <div className={`rounded-xl px-4 py-3 ${full ? "sm:col-span-2" : ""}`} style={{ background: "var(--paper)", border: "1px solid var(--line)" }}>
    <label className="flex items-center gap-2 text-xs font-semibold text-slate-500 mb-1.5"><Icon size={14} color="var(--brand)" /> {label}</label>
    {children}
  </div>
);

const strategyLabel = (key, t) => ({
  cheapest: t("loyalty.strategyCheapest"),
  efficient: t("loyalty.strategyEfficient"),
  safest: t("loyalty.strategySafest"),
}[key] || key);

const planCarriers = (plan) => {
  const carriers = [];
  (plan.runs || []).forEach((run) => {
    (run.segments || []).forEach((segment) => {
      const label = segment.marketing_carrier || segment.operating_carrier || segment.marketing_carrier_iata || segment.operating_carrier_iata;
      if (label && !carriers.includes(label)) carriers.push(label);
    });
  });
  return carriers;
};

const PlanCard = ({ plan, rank, tags, lang, t, onSelect }) => (
  <div className="rounded-2xl p-4" style={{ background: "#fff", border: `1px solid ${rank === 0 ? "var(--brand)" : "var(--line)"}` }}>
    <div className="flex flex-wrap items-start justify-between gap-3">
      <div>
        <div className="flex flex-wrap gap-1.5 mb-2">
          {rank === 0 && <span className="dv-pill text-[10px] px-2 py-0.5" style={{ background: "var(--brand-soft)", color: "var(--brand)" }}>{t("loyalty.bestPlan")}</span>}
          {tags.map((tag) => <span key={tag} className="dv-pill text-[10px] px-2 py-0.5" style={{ background: "var(--paper)", color: "var(--ink-soft)" }}>{strategyLabel(tag, t)}</span>)}
          {plan.requires_manual_validation && <span className="dv-pill text-[10px] px-2 py-0.5" style={{ background: "var(--wait-soft)", color: "var(--wait-ink)" }}>{t("loyalty.manualValidation")}</span>}
        </div>
        <div className="font-display font-bold text-lg">{t("loyalty.runCount", { n: plan.trip_count })}</div>
        <div className="text-xs text-slate-500 mt-1">{plan.xp_total} XP · {plan.eur_per_xp} €/XP · {t("loyalty.completeBy", { d: fmtDate(plan.completion_date, lang) })}</div>
        <div className="text-xs text-slate-700 mt-1 font-semibold">{planCarriers(plan).join(" · ") || t("loyalty.carrierUnknown")}</div>
      </div>
      <div className="text-right">
        <div className="dv-total text-2xl">{eur(plan.total_cost)}</div>
        <div className="text-xs" style={{ color: "var(--book-ink)" }}>+{plan.xp_total} XP {t("loyalty.projected")}</div>
      </div>
    </div>

    <div className="mt-4 space-y-2">
      {(plan.runs || []).map((run, i) => (
        <div key={run.offer_id || run.source_offer_id || i} className="flex items-center gap-3 rounded-xl px-3 py-2.5" style={{ background: "var(--paper)" }}>
          <div className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0" style={{ background: "var(--brand-soft)" }}><Plane size={14} color="var(--brand)" /></div>
          <div className="min-w-0 flex-1">
            <div className="font-display font-bold text-sm">{run.origin} → {run.destination} → {run.origin}</div>
            <div className="text-[11px] text-slate-500">{fmtDate(run.departure_date, lang)} → {fmtDate(run.return_date, lang)}</div>
          </div>
          <div className="text-right shrink-0">
            <div className="text-xs font-semibold" style={{ color: "var(--book-ink)" }}>+{run.xp_total} XP</div>
            <div className="text-xs text-slate-600">{eur(run.flight_total)}</div>
          </div>
        </div>
      ))}
    </div>
    <button type="button" onClick={onSelect} data-testid="loyalty-plan-detail" className="dv-btn dv-btn-ghost w-full mt-4 py-2.5">
      {t("loyalty.seePlan")} <ArrowLeft size={14} style={{ transform: "rotate(180deg)" }} />
    </button>
  </div>
);
